/**
 * @fileoverview Email verification page — confirms an email/password signup
 * with the 6-digit code emailed to the user, then continues into onboarding.
 * Reached from the signup form (after signup) and from login when an unverified
 * account tries to sign in. The email is passed via router state.
 */

import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import { inviteLanding } from "@/lib/pendingInvite";
import { getErrorMessage } from "@/lib/api";

const VerifyEmailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || "";

  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");

  // Without an email we can't verify — send the user back to sign in.
  if (!email) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-[var(--studio-base)]">
        <div className="text-center">
          <p className="text-[var(--text-secondary)] text-sm mb-4">We couldn't find which account to verify.</p>
          <Link to="/login" className="text-[var(--accent-yellow-text)] text-sm hover:underline">Back to sign in</Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^\d{6}$/.test(code)) {
      setError("Enter the 6-digit code from your email.");
      return;
    }
    setSubmitting(true);
    try {
      const result = await axios.post(
        `${API}/auth/verify-email`,
        { email, code },
        { withCredentials: true }
      );
      const user = result.data.user;
      const greeting = (user?.first_name || user?.name?.split(/\s+/)[0] || "").trim();
      toast.success(greeting ? `Welcome, ${greeting}!` : "Email verified!");
      // Signing up FROM an invitation ends here, one step later than the form
      // that started it — the stash is what carries it across that step. A new
      // account still owes onboarding, so for it the stash is left for
      // onboarding's last step to spend; only an account that has finished
      // onboarding goes to the invitation from here.
      navigate(inviteLanding(user) || "/onboarding", { state: { user } });
    } catch (err) {
      setError(getErrorMessage(err, "Verification failed. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    setError("");
    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email }, { withCredentials: true });
      toast.success("A new code is on its way.");
    } catch {
      toast.error("Could not resend the code. Please try again.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 studio-spotlight">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-6">
            <img src="/logo192.png" alt="Hans" className="h-10 rounded-lg" />
            <span className="text-lg font-semibold text-[var(--text-primary)] tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
              Hans
            </span>
          </div>
          <h1 className="text-2xl font-light text-[var(--text-primary)] mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            Check your email
          </h1>
          <p className="text-[var(--text-secondary)] text-sm">
            We sent a 6-digit code to <span className="text-[var(--text-primary)] font-medium">{email}</span>.
            Enter it below to finish setting up your account.
          </p>
        </div>

        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <div className="bg-[var(--studio-raised)] border border-[var(--studio-divider)] rounded-2xl p-6 space-y-4">
            <div>
              <Label className="text-[var(--text-tertiary)] text-xs uppercase tracking-wider">Verification Code</Label>
              <Input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => { setCode(e.target.value.replace(/\D/g, "").slice(0, 6)); if (error) setError(""); }}
                maxLength={6}
                aria-invalid={error ? "true" : undefined}
                className={`mt-1 bg-[var(--studio-base)] text-[var(--text-primary)] text-center text-2xl tracking-[0.5em] ${error ? "border-[var(--state-danger)]" : "border-[var(--studio-divider-strong)]"}`}
                placeholder="000000"
                autoFocus
              />
              {error && (
                <p className="text-[var(--state-danger)] text-xs mt-1" data-testid="code-error">{error}</p>
              )}
            </div>
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-[var(--accent-yellow)] text-[var(--text-inverse)] font-medium rounded-full py-3
                       hover:scale-105 active:scale-95 transition-transform text-sm disabled:opacity-50"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
            Verify
          </Button>
        </form>

        <div className="text-center mt-6 space-y-2">
          <p className="text-[var(--text-tertiary)] text-xs">
            Didn't get a code?{" "}
            <button onClick={handleResend} disabled={resending} className="text-[var(--accent-yellow-text)] hover:underline disabled:opacity-50">
              {resending ? "Sending…" : "Resend"}
            </button>
          </p>
          <Link to="/login" className="text-[var(--text-tertiary)] text-xs hover:text-[var(--text-primary)] transition-colors block">
            Back to sign in
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default VerifyEmailPage;
